import { NextRequest, NextResponse } from 'next/server';
import { logger } from '@/lib/logger';
import { retrievePaymentIntent } from '@/lib/stripe/server';
import { createClient } from '@/lib/supabase/server';
import { HTTP_STATUS_INTERNAL_SERVER_ERROR } from '@/lib/constants';

export async function GET(request: NextRequest) {
  try {
    const searchParams = request.nextUrl.searchParams;
    const paymentIntentId = searchParams.get('payment_intent');

    if (!paymentIntentId) {
      return NextResponse.json({ error: 'Missing payment_intent parameter' }, { status: 400 });
    }

    // Authenticate user
    const supabase = await createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return NextResponse.json({ error: 'Unauthorized' }, { status: 401 });
    }

    // Retrieve payment intent from Stripe
    const paymentIntent = await retrievePaymentIntent(paymentIntentId);

    // Check if user owns the payment by verifying the match belongs to their company
    const { data: payment } = await supabase
      .from('payments')
      .select('match_id, company_id, companies!inner(profile_id)')
      .eq('stripe_payment_id', paymentIntentId)
      .single();

    if (!payment || (payment.companies as { profileId?: string }).profileId !== user.id) {
      return NextResponse.json({ error: 'Payment not found or unauthorized' }, { status: 403 });
    }

    // Return payment intent status
    return NextResponse.json({
      status: paymentIntent.status,
      amount: paymentIntent.amount,
      currency: paymentIntent.currency,
    });
  } catch (error) {
    logger.error('Payment verification error:', error);
    if (error instanceof Error && error.message.includes('No such payment_intent')) {
      return NextResponse.json({ error: 'Payment not found' }, { status: 404 });
    }
    return NextResponse.json({ error: 'Failed to verify payment' }, { status: HTTP_STATUS_INTERNAL_SERVER_ERROR });
  }
}
